import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Box,
  Typography,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Backdrop,
} from "@mui/material";
import {
  Delete,
  Add as AddIcon,
  CloudUpload,
  InsertDriveFile,
  Image as ImageIcon,
  PictureAsPdf,
} from "@mui/icons-material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getCurrentTimezone } from "../../../utils/timezoneUtils";
import { useDropzone } from "react-dropzone";
import { useAtom } from "jotai";
import { fetchPartsList } from "../../../atoms/logistics/LogisticsManagementAtom";
import settingsAtom from "../../../atoms/settings/SettingsAtom";
import DeliveryTable from "./DeliveryTable";
import {
  generateUUID
} from "../../../services/logistics";
import axiosInstance from "../../../utils/axios";
import { isReadOnly } from "../../../utils/permissionUtils";

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const EditDeliveryModal = ({
  open,
  onClose,
  data,
  handleFetch,
  laydownList,
  partList,
  aliasName,
  isEdit,
  setIsEdit,
  customFields,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    comments: "",
    date: null,
    time: null,
    project_id: "",
    document: [],
    customResponses: {},
    deliveryRows: [
      {
        laydown: "",
        partDescription: "",
        partNumber: "",
        deliveredQty: 0,
        damagedQty: 0,
      },
    ],
  });

  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [, setParts] = useAtom(fetchPartsList);
  const [btnLoader, setBtnLoader] = useState(false);
  const [customFieldsFromResponse, setCustomFieldsFromResponse] = useState([]);
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [saveLoader, setSaveLoader] = useState(false);
  const [settingsData] = useAtom(settingsAtom);
  const { projectList } = settingsData;
  const currentProject = projectList.find(o => o.id === parseInt(localStorage.getItem("project_id")));

  const getInitialState = () => ({
    name: "",
    comments: "",
    date: null,
    time: null,
    document: [],
    customResponses: {},
    deliveryRows: [
      {
        laydown: "",
        partDescription: "",
        partNumber: "",
        deliveredQty: 0,
        damagedQty: 0,
        bom_id: "",
      },
    ],
  });

  const fetchCustomResponses = async (deliveryId) => {
    try {
      const response = await axiosInstance.get(
        `/projects/delivery?delivery_id=${deliveryId}`
      );
      if (response.data) {

        const customResponseData = response.data.custom_response;
        let fieldsArray = [];
        let customResponsesObj = {};

        if (Array.isArray(customResponseData)) {
          customResponseData.forEach((item) => {
            if (item.CustomInputsDelivery && item.CustomResponseDelivery) {
              const field = item.CustomInputsDelivery;
              const response = item.CustomResponseDelivery;

              fieldsArray.push({
                id: field.id,
                name: field.name,
                type: field.type,
                values: field.values,
                project_id: field.project_id,
              });

              if (
                field.name &&
                response.response !== undefined &&
                response.response !== null
              ) {
                customResponsesObj[field.name] = {
                  value: response.response,
                  delivery_id: response.delivery_id,
                  custom_response_delivery_id: response.id,
                  custom_input_delivery_id: response.custom_input_delivery_id,
                };
              }
            }
          });

          setCustomFieldsFromResponse(fieldsArray);
          return customResponsesObj;
        } else {
          if (
            customResponseData &&
            customResponseData.null &&
            Array.isArray(customResponseData.null)
          ) {
            fieldsArray = customResponseData.null
              .map((item) => (Array.isArray(item) ? item[0] : item))
              .filter((field) => field && field.name);
            setCustomFieldsFromResponse(fieldsArray);

            try {
              const customResponsesResponse = await axiosInstance.get(
                `/projects/delivery/custom_responses?delivery_id=${deliveryId}`
              );
              if (customResponsesResponse.data) {
                const fieldIdToNameMap = {};
                fieldsArray.forEach((field) => {
                  if (field && field.name && field.id) {
                    fieldIdToNameMap[field.id] = field.name;
                  }
                });

                customResponsesResponse.data.forEach((item) => {
                  const fieldName =
                    item.field_name || fieldIdToNameMap[item.custom_input_id];
                  if (fieldName) {
                    customResponsesObj[fieldName] = item.value;
                  } else {
                    console.warn("Could not map response to field name:", item);
                  }
                });

                return customResponsesObj;
              }
            } catch (customResponseError) {
              console.error(
                "No existing custom responses found or error fetching them:",
                customResponseError
              );
            }
          }
        }

        return customResponsesObj;
      }
      return {};
    } catch (error) {
      console.error("Error fetching delivery data:", error);
      return {};
    }
  };

  const fetchDeliveryData = async (deliveryId) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `/projects/delivery?delivery_id=${deliveryId}`
      );
      if (response.data) {
        await setParts(response.data.inventory?.[0]?.laydown_id);
      }
      const deliveryData = response.data?.delivery;
      const rows = response.data.inventory;

      const customResponsesData = await fetchCustomResponses(deliveryId);

      const bomDelivery = rows?.map((item) => {
        return {
          laydown: item?.laydown_id,
          partDescription: item?.bom_id,
          partNumber: item?.bom_id,
          deliveredQty: item?.count,
          damagedQty: item?.damaged,
          bom_id: item?.bom_id,
          bom_delivery_laydown_id: item?.bom_delivery_laydown_id,
          bom_laydown_id: item?.bom_laydown_id,
          delivery_laydown_id: item?.delivery_laydown_id,
        };
      });

      // Convert timestamp to timezone-aware dayjs object
      const timezone = getCurrentTimezone();
      const ms =
        Number(deliveryData.date_time) < 1e12
          ? Number(deliveryData.date_time) * 1000
          : Number(deliveryData.date_time);
      
      // Create dayjs object in the specified timezone
      const dt = dayjs(ms).tz(timezone);

      setFormData({
        id: deliveryData.id || "",
        name: deliveryData.name || "",
        comments: deliveryData.comments || "",
        date: dt,
        time: dt,
        project_id: aliasName?.project_id || "",
        document: deliveryData.documents || [],
        customResponses: customResponsesData,
        deliveryRows: bomDelivery || [
          {
            laydown: "",
            partDescription: "",
            partNumber: "",
            deliveredQty: 0,
            damagedQty: 0,
            bom_id: "",
          },
        ],
      });
    } catch (error) {
      console.error("Error fetching delivery data:", error);
      setFormData(getInitialState());
    } finally {
      setLoading(false);
    }
  };

  const prepareCustomResponsesData = (
    customResponses,
    customFieldsData,
    deliveryId = null
  ) => {
    if (!customResponses || Object.keys(customResponses).length === 0) {
      return [];
    }

    try {
      let fieldsArray =
        customFieldsFromResponse.length > 0 ? customFieldsFromResponse : [];

      if (fieldsArray.length === 0) {
        if (
          customFieldsData?.custom_inputs &&
          Array.isArray(customFieldsData.custom_inputs)
        ) {
          fieldsArray = customFieldsData.custom_inputs;
        } else if (Array.isArray(customFieldsData)) {
          fieldsArray = customFieldsData
            .map((item) => (Array.isArray(item) ? item[0] : item))
            .filter((field) => field && field.name);
        } else if (
          customFieldsData?.custom_response &&
          Array.isArray(customFieldsData.custom_response)
        ) {
          fieldsArray = customFieldsData.custom_response
            .map((item) => (Array.isArray(item) ? item[0] : item))
            .filter((field) => field && field.name);
        }
      }

      const fieldNameToIdMap = {};
      fieldsArray.forEach((field) => {
        if (
          field &&
          field.name &&
          field.id !== undefined &&
          field.id !== null
        ) {
          const id = Number(field.id);
          if (!isNaN(id) && Number.isInteger(id) && id > 0) {
            fieldNameToIdMap[field.name] = id;
          } else {
            console.warn(
              `Warning: Invalid field ID for ${field.name}: ${field.id}`
            );
          }
        }
      });

      if (Object.keys(fieldNameToIdMap).length === 0) {
        console.warn(
          "Warning: No valid field mappings found. Fields array:",
          fieldsArray
        );
        return [];
      }

      const validResponses = [];

      Object.entries(customResponses).forEach(([fieldName, responseData]) => {
        let value;
        if (typeof responseData === 'object' && responseData !== null) {
          value = responseData.value;
        } else {
          value = responseData;
        }

        if (value === null || value === undefined || value === "") {
          return;
        }

        const customInputId = fieldNameToIdMap[fieldName];
        if (!customInputId) {
          console.warn(
            `Warning: No valid ID found for field: ${fieldName}. Skipping this field.`
          );
          return;
        }

        try {
          const parsedId = Number(customInputId);
          if (isNaN(parsedId) || !Number.isInteger(parsedId) || parsedId <= 0) {
            console.warn(
              `Warning: Invalid custom_input_id for field ${fieldName}: ${customInputId}`
            );
            return;
          }

          const formattedResponse = {
            delivery_id: deliveryId || null,
            custom_input_delivery_id: parsedId,
            response: String(value).trim(),
          };

          validResponses.push(formattedResponse);
        } catch (err) {
          console.warn(
            `Warning: Could not format response for field ${fieldName}:`,
            err
          );
        }
      });

      if (validResponses.length === 0) {
        console.warn("No valid custom responses were prepared.");
        return [];
      }

      return validResponses;
    } catch (error) {
      console.error("Error preparing custom responses:", error);
      return [];
    }
  };

  const prepareCustomResponsesForUpdate = async (
    customResponses,
    customFieldsData,
    deliveryId
  ) => {
    if (!customResponses || Object.keys(customResponses).length === 0) {
      return [];
    }

    try {
      let fieldsArray =
        customFieldsFromResponse.length > 0 ? customFieldsFromResponse : [];

      if (fieldsArray.length === 0) {
        if (
          customFieldsData?.custom_inputs &&
          Array.isArray(customFieldsData.custom_inputs)
        ) {
          fieldsArray = customFieldsData.custom_inputs;
        } else if (Array.isArray(customFieldsData)) {
          fieldsArray = customFieldsData
            .map((item) => (Array.isArray(item) ? item[0] : item))
            .filter((field) => field && field.name);
        }
      }

      const fieldNameToIdMap = {};
      fieldsArray.forEach((field) => {
        if (
          field &&
          field.name &&
          field.id !== undefined &&
          field.id !== null
        ) {
          const id = Number(field.id);
          if (!isNaN(id) && Number.isInteger(id) && id > 0) {
            fieldNameToIdMap[field.name] = id;
          }
        }
      });

      const validResponses = [];

      Object.entries(customResponses).forEach(([fieldName, responseData]) => {
        let value, deliveryResponseId, customInputDeliveryId;

        if (typeof responseData === "object" && responseData !== null) {
          value = responseData.value;
          deliveryResponseId = responseData.custom_response_delivery_id;
          customInputDeliveryId = responseData.custom_input_delivery_id;
        } else {
          value = responseData;
          customInputDeliveryId = fieldNameToIdMap[fieldName];
        }

        if (!customInputDeliveryId) {
          customInputDeliveryId = fieldNameToIdMap[fieldName];
        }

        if (!customInputDeliveryId) {
          console.warn(
            `Warning: No valid ID found for field: ${fieldName}. Skipping this field.`
          );
          return;
        }

        try {
          const parsedId = Number(customInputDeliveryId);
          if (isNaN(parsedId) || !Number.isInteger(parsedId) || parsedId <= 0) {
            console.warn(
              `Warning: Invalid custom_input_delivery_id for field ${fieldName}: ${customInputDeliveryId}`
            );
            return;
          }

          const responseValue =
            value === null || value === undefined || value === ""
              ? null
              : String(value).trim();

          const formattedResponse = {
            delivery_id: deliveryId,
            custom_input_delivery_id: parsedId,
            response: responseValue,
          };

          if (deliveryResponseId) {
            formattedResponse.custom_response_delivery_id = deliveryResponseId;
          } else {
            if (responseValue === null) {
              return;
            }
          }

          validResponses.push(formattedResponse);
        } catch (err) {
          console.warn(
            `Warning: Could not format response for field ${fieldName}:`,
            err
          );
        }
      });

      return validResponses;
    } catch (error) {
      console.error("Error preparing custom responses for update:", error);
      return [];
    }
  };

  const handleSaveData = async (value) => {
    if (!value) return;

    setBtnLoader(true);

    try {
      const activeDeliveryRows = value.deliveryRows?.filter(row => !row.deleted) || [];

      const uniqueLaydowns = [
        ...new Set(
          activeDeliveryRows?.map((item) => item.laydown).filter(Boolean)
        ),
      ];

      const delivery_laydown = uniqueLaydowns;

      let custom_responses = [];
      if (
        value.customResponses &&
        Object.keys(value.customResponses).length > 0
      ) {
        if (value.id) {
          custom_responses = await prepareCustomResponsesForUpdate(
            value.customResponses,
            customFields,
            value.id
          );
        } else {
          custom_responses = prepareCustomResponsesData(
            value.customResponses,
            customFields,
            value.id
          );
        }

        custom_responses = custom_responses.map(
          ({ delivery_id, ...rest }) => rest
        );
      }

      const result = {
        ...(value.id && { id: value.id }),
        name: value.name || "",
        comments: value.comments || "",
        date_time: value.date_time || "",
        delivery_laydown: delivery_laydown,
        document: value.document || [],
        custom_responses: custom_responses,
      };

      const delivery =
        activeDeliveryRows?.map((item) => ({
          ...item,
          laydown_id: item?.laydown,
          count: item?.deliveredQty,
          damaged: item?.damagedQty,
          bom_id: item?.bom_id,
          received: true,
          time: value.date_time ,
          purpose_code: 0,
          comments: "",
        })) || [];

      const keysToRemove = [
        "deliveredQty",
        "damagedQty",
        "partDescription",
        "partNumber",
        "laydown",
      ];

      result.bom_delivery_laydown = delivery.map(({ ...item }) => {
        keysToRemove.forEach((key) => delete item[key]);
        return item;
      });

      const res = await axiosInstance.post(
        `/projects/delivery-with-inventory`,
        [result]
      );

      if (res) {
        handleFetch();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsEdit(false);
      setBtnLoader(false);
    }
  };

  useEffect(() => {
    if (open) {
      if (data && data.id) {
        fetchDeliveryData(data.id);
      } else {
        setLoading(true);

        const initializeNewDelivery = async () => {
          try {
            const setupResponse = await axiosInstance.get(
              `/projects/delivery/custom_inputs`
            );
            if (setupResponse.data && setupResponse.data.custom_inputs) {
              setCustomFieldsFromResponse(setupResponse.data.custom_inputs);
            }
          } catch (error) {
            console.error("Could not fetch custom fields setup:", error);
          }

          setFormData(getInitialState());
          setLoading(false);
        };

        const timeout = setTimeout(initializeNewDelivery, 500);
        return () => clearTimeout(timeout);
      }
    }

    if (open && customFields) {
      let fieldsArray = [];
      if (
        customFields?.custom_inputs &&
        Array.isArray(customFields.custom_inputs)
      ) {
        fieldsArray = customFields.custom_inputs;
      } else if (Array.isArray(customFields)) {
        fieldsArray = customFields
          .map((item) => (Array.isArray(item) ? item[0] : item))
          .filter((field) => field && field.name);
      } else if (
        customFields?.custom_response &&
        Array.isArray(customFields.custom_response)
      ) {
        fieldsArray = customFields.custom_response
          .map((item) => (Array.isArray(item) ? item[0] : item))
          .filter((field) => field && field.name);
      }

      fieldsArray.forEach((field) => {
        // Field processing logic if needed
      });
    }
    // eslint-disable-next-line
  }, [open, data, aliasName, customFields, currentProject]);

  const handleChange = (field) => (e) => {
    if (!isEdit) return;
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleCustomFieldChange = (fieldName, value) => {
    if (!isEdit) return;

    setFormData((prev) => {
      const existingResponse = prev.customResponses[fieldName];

      let newResponse;
      if (
        typeof existingResponse === "object" &&
        existingResponse !== null &&
        existingResponse.custom_response_delivery_id
      ) {
        newResponse = {
          ...existingResponse,
          value: value === "" || value === undefined ? null : value,
        };
      } else {
        newResponse =
          value === "" || value === undefined || value === "" ? null : value;
      }

      return {
        ...prev,
        customResponses: {
          ...prev.customResponses,
          [fieldName]: newResponse,
        },
      };
    });
  };

  const handleDateChange = (newDate) => {
    if (!isEdit) return;
    const timezone = getCurrentTimezone();
    // Ensure the date is in the correct timezone
    const dateInTimezone = newDate ? newDate.tz(timezone) : null;
    setFormData((prev) => ({ ...prev, date: dateInTimezone }));
  };

  const handleTimeChange = (newTime) => {
    if (!isEdit) return;
    const timezone = getCurrentTimezone();
    // Ensure the time is in the correct timezone
    const timeInTimezone = newTime ? newTime.tz(timezone) : null;
    setFormData((prev) => ({ ...prev, time: timeInTimezone }));
  };

  const handleDrop = useCallback(
    async (acceptedFiles) => {
      if (!isEdit) return;

      setUploadLoading(true);
      const uploadedDocs = [];

      const existingFileNames = (formData.document || []).map(
        (doc) => doc.name
      );
      const newFiles = acceptedFiles.filter(
        (file) => !existingFileNames.includes(file.name)
      );

      if (newFiles.length !== acceptedFiles.length) {
        console.warn(
          `${acceptedFiles.length - newFiles.length
          } files skipped (already exist)`
        );
      }

      for (const file of newFiles) {
        const formDataUpload = new FormData();
        const imageId = generateUUID();
        const type = "delivery";

        formDataUpload.append("image_id", imageId);
        formDataUpload.append("type", type);
        formDataUpload.append("file", file);

        try {
          const res = await axiosInstance.post(
            `/file_upload/upload/document?image_id=${imageId}&type=${type}`,
            formDataUpload,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );

          if (!res) throw new Error("Upload failed");

          uploadedDocs.push({
            upload_id: res.data,
            name: file.name,
            size: file.size,
            type: file.type,
            uploadedAt: new Date().toISOString(),
          });
        } catch (error) {
          console.error(`Upload error for file ${file.name}:`, error);
        }
      }

      setFormData((prev) => {
        const currentDocuments = prev.document || [];
        const updatedDocuments = [...currentDocuments, ...uploadedDocs];

        return {
          ...prev,
          document: updatedDocuments,
        };
      });

      setUploadLoading(false);
    },
    [isEdit, formData.document]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".jpg", ".jpeg", ".png"],
    },
    multiple: true,
    maxSize: 10 * 1024 * 1024,
    disabled: !isEdit,
  });

  const handleDeliveryRowChange = async (index, field, value) => {
    if (!isEdit) return;

    if (!partList.length) {
      await setParts(value);
    }
    setFormData((prev) => {
      const updatedRows = [...prev.deliveryRows];
      if (field === "bom_id") {
        updatedRows[index][field] = Number(value);
        updatedRows[index]["partDescription"] = Number(value);
        updatedRows[index]["partNumber"] = Number(value);
      } else if (["deliveredQty", "damagedQty"].includes(field)) {
        updatedRows[index][field] = Number(value);
      } else {
        updatedRows[index][field] = value;
      }
      return { ...prev, deliveryRows: updatedRows };
    });
  };

  const handleAddDeliveryRow = () => {
    if (!isEdit) return;

    setFormData((prev) => ({
      ...prev,
      deliveryRows: [
        ...(prev.deliveryRows || []),
        {
          laydown: "",
          partDescription: "",
          partNumber: "",
          deliveredQty: 0,
          damagedQty: 0,
          bom_id: "",
        },
      ],
    }));
  };

  const handleDeleteDeliveryRow = (value) => {
    if (!isEdit) return;

    setFormData((prev) => {
      const updatedRows = [...prev.deliveryRows];

      const data = updatedRows?.map((item) => {
        if (value?.bom_delivery_laydown_id === item?.bom_delivery_laydown_id) {
          return {
            ...item,
            delete: true
          }
        } else {
          return item
        }
      })

      return { ...prev, deliveryRows: data };
    });
  };

  const handleFileDelete = (index) => {
    if (!isEdit) return;

    setFormData((prev) => {
      const currentDocs = prev.document || [];

      if (index < 0 || index >= currentDocs.length) {
        console.error("Invalid file index for deletion:", index);
        return prev;
      }

      const newFiles = currentDocs.filter((_, i) => i !== index);

      return {
        ...prev,
        document: newFiles,
      };
    });
  };

  const handleSubmit = () => {
    setSaveLoader(true);
    const timezone = getCurrentTimezone();
    
    let unixTimestamp = null;
    if (formData.date && formData.time) {
      const dateTz = formData.date.tz(timezone);
      const timeTz = formData.time.tz(timezone);
      const combined = dateTz
        .hour(timeTz.hour())
        .minute(timeTz.minute())
        .second(0)
        .millisecond(0);
      unixTimestamp = combined.tz('UTC').unix();
    } else if (formData.date) {
      unixTimestamp = formData.date.tz(timezone).tz('UTC').startOf('day').unix();
    }

    handleSaveData({
      ...formData,
      date_time: unixTimestamp,
    });

    setIsEdit(false);
    setTimeout(() => {
      setSaveLoader(false);
      onClose();
    }, 500);
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes("pdf")) {
      return <PictureAsPdf sx={{ color: "#d32f2f" }} />;
    } else if (fileType?.includes("image")) {
      return <ImageIcon sx={{ color: "#1976d2" }} />;
    }
    return <InsertDriveFile sx={{ color: "#757575" }} />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleDocumentClick = (document) => {
    setSelectedDocument(document);
    setDocumentModalOpen(true);
  };

  const handleDocumentModalClose = () => {
    setDocumentModalOpen(false);
    setSelectedDocument(null);
  };

  const renderCustomField = (field) => {
    const fieldName = field.name;
    const fieldType = field.type;
    const responseData = formData.customResponses[fieldName];
    const timezone = currentProject?.timezone || 'Pacific/Auckland';

    const fieldValue =
      typeof responseData === "object" && responseData !== null
        ? responseData.value || ""
        : responseData || "";

    switch (fieldType) {
      case 0: // Number
        return (
          <TextField
            fullWidth
            type="number"
            placeholder={`Enter ${fieldName}`}
            value={fieldValue}
            onChange={(e) => handleCustomFieldChange(fieldName, e.target.value)}
            disabled={!isEdit}
          />
        );
      case 1: // Decimal
        return (
          <TextField
            fullWidth
            type="number"
            step="0.01"
            placeholder={`Enter ${fieldName}`}
            value={fieldValue}
            onChange={(e) => handleCustomFieldChange(fieldName, e.target.value)}
            disabled={!isEdit}
          />
        );
      case 2: // Text
        return (
          <TextField
            fullWidth
            placeholder={`Enter ${fieldName}`}
            value={fieldValue}
            onChange={(e) => handleCustomFieldChange(fieldName, e.target.value)}
            disabled={!isEdit}
          />
        );
      case 3: // Dropdown
        const dropdownOptions = Array.isArray(field.values)
          ? field.values
          : typeof field.values === "string" && field.values.trim()
            ? field.values
              .split(",")
              .map((v) => v.trim())
              .filter((v) => v)
            : [];

        if (!isEdit) {
          return (
            <TextField
              fullWidth
              value={fieldValue || "No selection"}
              disabled={true}
              InputProps={{
                readOnly: true,
              }}
            />
          );
        }

        return (
          <FormControl fullWidth disabled={!isEdit}>
            <InputLabel>{`Select ${fieldName}`}</InputLabel>
            <Select
              value={fieldValue}
              onChange={(e) =>
                handleCustomFieldChange(fieldName, e.target.value)
              }
              label={`Select ${fieldName}`}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {dropdownOptions.map((option, index) => (
                <MenuItem key={index} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 4: // Date
        if (!isEdit) {
          return (
            <TextField
              fullWidth
              value={
                fieldValue
                  ? dayjs(fieldValue).tz(timezone).format("DD MMM YYYY")
                  : "No date selected"
              }
              disabled={true}
              InputProps={{
                readOnly: true,
              }}
            />
          );
        }
        return (
          <DatePicker
            value={fieldValue ? dayjs(fieldValue).tz(timezone) : null}
            onChange={(newValue) =>
              handleCustomFieldChange(
                fieldName,
                newValue ? newValue.tz(timezone).format("DD MMM YYYY") : ""
              )
            }
            disabled={!isEdit}
            slotProps={{
              textField: {
                fullWidth: true,
                disabled: !isEdit,
              },
            }}
          />
        );
      case 5: // Time
        if (!isEdit) {
          return (
            <TextField
              fullWidth
              value={fieldValue || "No time selected"}
              disabled={true}
              InputProps={{
                readOnly: true,
              }}
            />
          );
        }
        return (
          <TimePicker
            value={fieldValue ? dayjs(fieldValue, "HH:mm").tz(timezone) : null}
            onChange={(newValue) =>
              handleCustomFieldChange(
                fieldName,
                newValue ? newValue.tz(timezone).format("HH:mm") : ""
              )
            }
            disabled={!isEdit}
            slotProps={{
              textField: {
                fullWidth: true,
                disabled: !isEdit,
              },
            }}
          />
        );
      case 6: // Comments
        return (
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder={`Enter ${fieldName}`}
            value={fieldValue}
            onChange={(e) => handleCustomFieldChange(fieldName, e.target.value)}
            disabled={!isEdit}
            InputProps={{
              readOnly: !isEdit,
            }}
          />
        );
      default:
        return (
          <TextField
            fullWidth
            placeholder={`Enter ${fieldName}`}
            value={fieldValue}
            onChange={(e) => handleCustomFieldChange(fieldName, e.target.value)}
            disabled={!isEdit}
            InputProps={{
              readOnly: !isEdit,
            }}
          />
        );
    }
  };

  const visibleDeliveryRows = formData.deliveryRows?.filter(row => !row.deleted) || [];


  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={3}
        pt={2}
        pb={1}
      >
        <Typography variant="h6" fontWeight={600}>
          {data && data.id ? "Edit Delivery" : "Add Delivery"}
        </Typography>
        {isEdit ? (
          <Box sx={{ display: "flex", gap: 2, pr: 2 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={btnLoader}
              variant="contained"
            >
              {btnLoader ? <CircularProgress size={20} /> : "Save"}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: "flex", gap: 2, pr: 2 }}>
            <Button onClick={onClose}>Close</Button>
            <Button variant="contained" onClick={() => setIsEdit(true)} disabled ={isReadOnly()}>
              Edit
            </Button>
          </Box>
        )}
      </Box>
      <DialogContent>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            p={3}
            height={400}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3} mt={1}>
            <Box>
              <Typography fontWeight={500} mb={0.5}>
               {aliasName ? aliasName : "Delivery Name" }
              </Typography>
              <TextField
                fullWidth
                placeholder={aliasName ? `Enter ${aliasName}` : "Enter delivery name"}
                value={formData.name}
                onChange={handleChange("name")}
                disabled={!isEdit}
              />
            </Box>

            <Stack direction="row" spacing={2}>
              <Box flex={1}>
                <Typography fontWeight={500} mb={0.5}>
                  Delivery Date
                </Typography>
                {!isEdit ? (
                  <TextField
                    fullWidth
                    value={formData.date ? dayjs(formData.date).format("DD MMM YYYY") : "No date selected"}
                    disabled={true}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                ) : (
                  <DatePicker
                    value={formData.date}
                    onChange={handleDateChange}
                    disabled={!isEdit}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        disabled: !isEdit,
                      },
                    }}
                  />
                )}
              </Box>
              <Box flex={1}>
                <Typography fontWeight={500} mb={0.5}>
                  Delivery Time
                </Typography>
                <TimePicker
                  value={formData.time}
                  onChange={handleTimeChange}
                  disabled={!isEdit}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      disabled: !isEdit,
                    },
                  }}
                />
              </Box>
            </Stack>

            <Box>
              <Typography fontWeight={500} mb={0.5}>
                Comments
              </Typography>
              <TextField
                fullWidth
                placeholder="Additional comments"
                value={formData.comments}
                onChange={handleChange("comments")}
                multiline
                rows={3}
                disabled={!isEdit}
              />
            </Box>

            {(() => {
              let fieldsToRender = [];

              if (
                customFields?.custom_inputs &&
                Array.isArray(customFields.custom_inputs)
              ) {
                fieldsToRender = customFields.custom_inputs;
              } else if (Array.isArray(customFields)) {
                fieldsToRender = customFields
                  .map((item) => (Array.isArray(item) ? item[0] : item))
                  .filter((field) => field && field.name);
              } else if (customFields && typeof customFields === "object") {
                const customResponseData = customFields.custom_response;
                if (Array.isArray(customResponseData)) {
                  fieldsToRender = customResponseData
                    .map((item) => (Array.isArray(item) ? item[0] : item))
                    .filter((field) => field && field.name);
                }
              }

              return (
                fieldsToRender.length > 0 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Additional Information
                    </Typography>
                    <Stack spacing={2}>
                      {fieldsToRender.map((field) => (
                        <Box key={field.id || field.name}>
                          <Typography fontWeight={500} mb={0.5}>
                            {field.name}
                          </Typography>
                          {renderCustomField(field)}
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )
              );
            })()}

            <Box>
              <Typography fontWeight={500} mb={1}>
                Documents
                {formData?.document?.length > 0 && (
                  <Chip
                    label={`${formData.document.length} file${formData.document.length !== 1 ? "s" : ""
                      }`}
                    size="small"
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>

              {isEdit && (
                <Paper
                  variant="outlined"
                  {...getRootProps()}
                  sx={{
                    borderStyle: "dashed",
                    borderWidth: 2,
                    borderColor: isDragActive ? "primary.main" : "grey.300",
                    padding: 4,
                    textAlign: "center",
                    cursor: "pointer",
                    bgcolor: isDragActive ? "action.hover" : "grey.50",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      borderColor: "primary.main",
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <input {...getInputProps()} />
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap={1}
                  >
                    <CloudUpload sx={{ fontSize: 48, color: "grey.400" }} />
                    <Typography
                      variant="h6"
                      color="textPrimary"
                      fontWeight={500}
                    >
                      {formData?.document?.length > 0
                        ? "Add More Files (Click or Drag)"
                        : "Click or Drag Files Here (Multiple)"}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Supported files: JPG, PNG, PDF (Max 10MB each)
                    </Typography>
                    {uploadLoading && (
                      <Box mt={1}>
                        <CircularProgress size={24} />
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          mt={0.5}
                        >
                          Uploading files...
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              )}

              {/* Uploaded Files List */}
              {formData?.document?.length > 0 && (
                <Box mt={2}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    mb={1}
                    color="textSecondary"
                  >
                    {isEdit ? "Uploaded Files" : "Documents"} (
                    {formData.document.length})
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{ maxHeight: 300, overflow: "auto" }}
                  >
                    <List dense>
                      {formData.document.map((file, index) => (
                        <ListItem
                          key={`${file.upload_id || file.name}-${index}`}
                          onClick={() => handleDocumentClick(file)}
                          sx={{
                            borderBottom:
                              index < formData.document.length - 1
                                ? "1px solid"
                                : "none",
                            borderBottomColor: "divider",
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                          }}
                          secondaryAction={
                            isEdit && (
                              <IconButton
                                edge="end"
                                onClick={() => handleFileDelete(index)}
                                size="small"
                                sx={{ color: "error.main" }}
                              >
                                <Delete />
                              </IconButton>
                            )
                          }
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            {getFileIcon(file.type)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight={500}>
                                {file.name}
                              </Typography>
                            }
                            secondary={
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                mt={0.5}
                                flexWrap="wrap"
                              >
                                <Chip
                                  label={formatFileSize(file.size || 0)}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: "0.7rem" }}
                                />
                                {file.type && (
                                  <Chip
                                    label={
                                      file.type.split("/")[1]?.toUpperCase() ||
                                      "FILE"
                                    }
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: "0.7rem" }}
                                  />
                                )}
                                {file.upload_id && (
                                  <Chip
                                    label={`ID: ${file.upload_id}`}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: "0.7rem" }}
                                  />
                                )}
                                {file.uploadedAt && (
                                  <Chip
                                    label="New"
                                    size="small"
                                    color="info"
                                    sx={{ height: 20, fontSize: "0.7rem" }}
                                  />
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Box>
              )}
            </Box>

            <Box>
              <Typography variant="h6" gutterBottom>
                Delivery Details
              </Typography>
              <DeliveryTable
                deliveryRows={visibleDeliveryRows?.filter(item => item.delete !== true)} // Pass filtered rows to table
                laydownList={laydownList}
                partList={partList}
                handleDeliveryRowChange={handleDeliveryRowChange}
                handleDeleteDeliveryRow={handleDeleteDeliveryRow}
                isEdit={isEdit}
              />

              {isEdit && (
                <Box mt={1} textAlign="right">
                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    onClick={handleAddDeliveryRow}
                    size="small"
                  >
                    Add Item
                  </Button>
                </Box>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <Backdrop
        open={saveLoader}
        sx={{ zIndex: 9999 }}
      >
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress size={40} />
          <Typography variant="h6" >
            Saving...
          </Typography>
        </Box>
      </Backdrop>
      <documentModalOpen
        open={documentModalOpen}
        onClose={handleDocumentModalClose}
        document={selectedDocument}
      />
    </Dialog>
  );
};

export default EditDeliveryModal;